import React from 'react';
import { pxToRem } from '@withjoy/joykit/theme';
import { Box, ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { Car, DriveTime, LightningBolt, MapMarker, Profile, Settings } from '@withjoy/joykit/icons';

import { useTranslation } from '@shared/core';
import { CarRateProvider } from '@graphql/generated-base';
import { UserRating } from '@apps/stays/components/Ratings';
import { distanceInMiles } from '@shared/utils/distanceInMiles';
import { CAR_MIN_REVIEW_COUNT } from '../../CarRentalConsole.constants';
import { useCarAttributeLabels } from '../../hooks/useCarAttributeLabels';
import { formatCarMoney, getCarDiscountPercentage } from '../../formatCarMoney';
import type { CarSearchLocation, CarVehicle } from '../../CarRentalConsole.types';
import { useFormatTravelDistance } from '@shared/utils/hooks/useFormatTravelDistance';

type CarListingEntryProps = Readonly<{
  vehicle: CarVehicle;
  isReserving?: boolean;
  navigatorLanguage: string;
  isReserveDisabled?: boolean;
  onReserve: (vehicle: CarVehicle) => void;
  onViewDetails: (vehicle: CarVehicle) => void;
  searchedPickUpLocation?: CarSearchLocation | null;
  searchedDropOffLocation?: CarSearchLocation | null;
}>;

type ProviderLocation = CarVehicle['pickUpLocation'];

const getLocationAddress = (location: ProviderLocation): string | null => {
  const address = location?.address;
  if (!address) return null;

  const stateAndPostalCode = [address.stateProvinceName, address.postalCode].filter(Boolean).join(' ');
  return [address.line1, address.line2, address.city, stateAndPostalCode].filter(Boolean).join(', ') || null;
};

const getSearchLocationName = (location: CarSearchLocation | null | undefined): string | null => location?.displayName || location?.address || location?.city || null;

const getLocationDistance = (searchedLocation: CarSearchLocation | null | undefined, providerLocation: ProviderLocation): number | null => {
  if (searchedLocation?.lat === undefined || searchedLocation.lng === undefined || !providerLocation?.coordinates) return null;

  return distanceInMiles(
    { lat: searchedLocation.lat, lng: searchedLocation.lng },
    {
      latitude: providerLocation.coordinates.latitude,
      longitude: providerLocation.coordinates.longitude,
    },
  );
};

const Detail = ({ icon, children }: React.PropsWithChildren<{ icon: React.ReactNode }>) => (
  <Flex
    gap={3}
    minWidth={0}
    alignItems="center"
  >
    <Box
      color="mono12"
      flexShrink={0}
    >
      {icon}
    </Box>
    <Box
      color="mono14"
      typographyVariant="body1"
    >
      {children}
    </Box>
  </Flex>
);

type LocationDetailProps = Readonly<{
  kind: 'pickUp' | 'dropOff';
  location: ProviderLocation;
  searchedLocation?: CarSearchLocation | null;
}>;

type PricingPanelProps = Readonly<{
  isExpedia: boolean;
  isReserving: boolean;
  onReserve: () => void;
  onViewDetails: () => void;
  isReserveDisabled: boolean;
  discountPercentage: number | null;
  formattedTotalRate: string | null;
  formattedDailyRate: string | null;
  formattedTotalStrikethrough: string | null;
}>;

const PricingPanel = ({
  isExpedia,
  onReserve,
  isReserving,
  onViewDetails,
  isReserveDisabled,
  discountPercentage,
  formattedTotalRate,
  formattedDailyRate,
  formattedTotalStrikethrough,
}: PricingPanelProps) => {
  const { tq } = useTranslation('carRentalConsole');

  if (!formattedTotalRate && !formattedDailyRate) {
    return null;
  }

  return (
    <Flex
      gap={3}
      flexShrink={0}
      alignItems="flex-start"
      justifyContent="flex-end"
      minWidth={{ sm2: pxToRem(180) }}
      width={{ _: '100%', sm2: 'auto' }}
    >
      <Flex
        gap={2}
        flexDirection="column"
        alignItems={{ _: 'flex-start', sm2: 'flex-end' }}
      >
        {discountPercentage ? (
          <Box
            paddingY={1}
            paddingX={3}
            color="white"
            borderRadius={2}
            whiteSpace="nowrap"
            typographyVariant="label2"
            backgroundColor="positive6"
          >
            {tq`${discountPercentage}% off`}
          </Box>
        ) : null}
        <Flex
          gap={2}
          flexWrap="wrap"
          alignItems="baseline"
          justifyContent={{ _: 'flex-start', sm2: 'flex-end' }}
        >
          {formattedTotalStrikethrough ? (
            <TextV2
              color="mono10"
              typographyVariant="label2"
              textDecoration="line-through"
            >
              {formattedTotalStrikethrough}
            </TextV2>
          ) : null}
          <TextV2
            color="mono14"
            typographyVariant="hed4"
          >
            {formattedTotalRate ?? formattedDailyRate}
          </TextV2>
        </Flex>
        <TextV2
          color="mono10"
          typographyVariant="label2"
          textAlign={{ sm2: 'right' }}
        >
          {tq`includes taxes & fees`}
        </TextV2>
        {formattedDailyRate ? (
          <TextV2
            color="mono10"
            typographyVariant="label2"
            textAlign={{ sm2: 'right' }}
          >
            {tq`${formattedDailyRate} / day`}
          </TextV2>
        ) : null}

        <ButtonV2
          variant="solid"
          shape="rounded"
          intent="primary"
          minWidth={pxToRem(120)}
          typographyVariant="button1"
          width={{ _: '100%', sm2: 'auto' }}
          loading={isExpedia ? undefined : isReserving}
          disabled={isExpedia ? undefined : isReserveDisabled}
          onClick={() => (isExpedia ? onViewDetails() : onReserve())}
        >
          {tq`Reserve`}
        </ButtonV2>
      </Flex>
    </Flex>
  );
};

const LocationDetail = ({ kind, location, searchedLocation }: LocationDetailProps) => {
  const { tq } = useTranslation('carRentalConsole');
  const formatTravelDistance = useFormatTravelDistance();
  if (!location) return null;

  const cityOrName = location.address?.city || location.name;
  if (!cityOrName) return null;

  const isAirport = location.type?.toLowerCase() === 'airport';

  const heading =
    kind === 'pickUp'
      ? isAirport
        ? tq`Pick-up at ${cityOrName} Airport`
        : tq`Pick-up in ${cityOrName}`
      : isAirport
        ? tq`Drop-off at ${cityOrName} Airport`
        : tq`Drop-off in ${cityOrName}`;

  const address = getLocationAddress(location);
  const searchedLocationName = getSearchLocationName(searchedLocation);
  const distance = isAirport ? null : getLocationDistance(searchedLocation, location);
  const formattedDistance = distance !== null ? formatTravelDistance({ value: distance, type: 'distance' }) : null;
  const distanceWithUnit = formattedDistance ? `${formattedDistance.formatted} ${formattedDistance.unitLabel}` : null;

  return (
    <Flex
      flex={1}
      minWidth={0}
    >
      <Detail icon={<MapMarker size={18} />}>
        <Flex
          gap={1}
          minWidth={0}
          flexDirection="column"
        >
          <TextV2
            color="mono14"
            typographyVariant="hed1"
          >
            {heading}
          </TextV2>
          {distanceWithUnit && searchedLocationName ? (
            <TextV2
              color="mono12"
              typographyVariant="label2"
            >
              {tq`${distanceWithUnit} from ${searchedLocationName}`}
            </TextV2>
          ) : null}
          {address ? (
            <TextV2
              color="mono12"
              typographyVariant="label2"
            >
              {address}
            </TextV2>
          ) : null}
        </Flex>
      </Detail>
    </Flex>
  );
};

const CarListingEntryInner = ({
  vehicle,
  onReserve,
  onViewDetails,
  navigatorLanguage,
  isReserving = false,
  searchedPickUpLocation,
  searchedDropOffLocation,
  isReserveDisabled = false,
}: CarListingEntryProps) => {
  const { tq } = useTranslation('carRentalConsole');

  const isExpedia = vehicle.provider === CarRateProvider.EXPEDIA;

  const { formatSpecification, formatVehicleCategory, formatVehicleSize } = useCarAttributeLabels();
  const title = vehicle.image?.caption || [formatVehicleSize(vehicle.vehicle.size), formatVehicleCategory(vehicle.vehicle.category)].join(' ');
  const isElectric = vehicle.vehicle.fuelType.toLowerCase().includes('electric');
  const isFullyRefundable = (vehicle.cancellationPolicies?.length ?? 0) === 0;

  const rating = vehicle.pickUpLocation?.rating ?? vehicle.dropOffLocation?.rating;
  const imageUrl = vehicle.image?.url;

  const { pricing } = vehicle;

  const dailyRate = pricing.dailyRate;
  const dailyRateStrikethrough = pricing.dailyRateStrikethrough;

  const totalRate = pricing.totalInclusive;
  const totalRateStrikethrough = pricing.totalInclusiveStrikethrough;

  const formattedDailyRate = dailyRate ? formatCarMoney(dailyRate, navigatorLanguage) : null;
  const formattedTotalRate = totalRate ? formatCarMoney(totalRate, navigatorLanguage) : null;
  const formattedTotalStrikethrough = totalRateStrikethrough ? formatCarMoney(totalRateStrikethrough, navigatorLanguage) : null;

  const discountPercentage =
    totalRate && totalRateStrikethrough
      ? getCarDiscountPercentage(totalRate, totalRateStrikethrough)
      : dailyRate && dailyRateStrikethrough
        ? getCarDiscountPercentage(dailyRate, dailyRateStrikethrough)
        : null;

  return (
    <Flex
      borderRadius={5}
      overflow="hidden"
      border="solid 1px"
      borderColor="mono3"
      minHeight={{ sm2: pxToRem(230) }}
      flexDirection={{ _: 'column', sm2: 'row' }}
    >
      <Box
        padding={4}
        flexShrink={0}
        backgroundColor="white"
        alignSelf={{ sm2: 'stretch' }}
        minWidth={{ sm2: pxToRem(250) }}
        maxWidth={{ sm2: pxToRem(420) }}
        width={{ _: '100%', sm2: '16%' }}
        borderRightWidth={{ _: 0, sm2: '1px' }}
        borderBottomWidth={{ _: '1px', sm2: 0 }}
        height={{ _: pxToRem(220), sm2: 'auto' }}
        borderRightStyle={{ _: 'none', sm2: 'solid' }}
        borderBottomStyle={{ _: 'solid', sm2: 'none' }}
        borderRightColor={{ _: 'transparent', sm2: 'mono3' }}
        borderBottomColor={{ _: 'mono3', sm2: 'transparent' }}
      >
        {imageUrl ? (
          <Box
            as="img"
            width="100%"
            height="100%"
            src={imageUrl}
            objectFit="contain"
            objectPosition="center"
            alt={vehicle.description || tq`Rental car`}
          />
        ) : (
          <Flex
            role="img"
            width="100%"
            height="100%"
            color="mono10"
            alignItems="center"
            justifyContent="center"
            aria-label={vehicle.description || tq`Rental car`}
          >
            <Car size={48} />
          </Flex>
        )}
      </Box>
      <Flex
        gap={6}
        flex={1}
        padding={6}
        minWidth={0}
        alignItems="stretch"
        flexDirection={{ _: 'column', sm2: 'row' }}
      >
        <Flex
          gap={4}
          flex={1}
          minWidth={0}
          flexDirection="column"
        >
          <Flex
            gap={6}
            width="100%"
            alignItems="flex-start"
            flexDirection={{ _: 'column', sm2: 'row' }}
          >
            <Flex
              gap={4}
              minWidth={0}
              flex={{ sm2: 1 }}
              flexDirection="column"
            >
              <Box minWidth={0}>
                <TextV2
                  color="mono14"
                  typographyVariant="hed3"
                >
                  {title}
                </TextV2>
                {vehicle.description ? (
                  <TextV2
                    marginTop={2}
                    color="mono12"
                    typographyVariant="body1"
                  >
                    {vehicle.description}
                  </TextV2>
                ) : null}
              </Box>
              <Flex
                gap={3}
                alignItems="center"
              >
                {vehicle.vehicle.passengers ? <Detail icon={<Profile size={18} />}>{vehicle.vehicle.passengers}</Detail> : null}
                {vehicle.vehicle.transmissionType ? <Detail icon={<Settings size={18} />}>{formatSpecification(vehicle.vehicle.transmissionType)}</Detail> : null}
                {vehicle.mileageTracking ? <Detail icon={<DriveTime size={18} />}>{vehicle.mileageTracking}</Detail> : null}
              </Flex>

              <LocationDetail
                kind="pickUp"
                location={vehicle.pickUpLocation}
                searchedLocation={searchedPickUpLocation}
              />
            </Flex>

            <Flex
              gap={4}
              minWidth={0}
              flex={{ sm2: 1 }}
              flexDirection="column"
            >
              <Flex
                gap={2}
                flexDirection="column"
              >
                {isElectric && (
                  <Flex
                    gap={2}
                    color="positive7"
                    alignItems="center"
                  >
                    <LightningBolt size={18} />
                    <TextV2 typographyVariant="body1">{tq`Fully electric`}</TextV2>
                  </Flex>
                )}
                <TextV2
                  typographyVariant="label2"
                  color="positive7"
                >
                  {isFullyRefundable ? tq`Fully refundable` : tq`Cancellation available`}
                </TextV2>
                <TextV2
                  color="positive7"
                  typographyVariant="label2"
                >
                  {tq`Pay now and save`}
                </TextV2>
              </Flex>
              {vehicle.vendor.imageUrl || vehicle.vendor.name ? (
                <Flex
                  gap={3}
                  flexWrap="wrap"
                  alignItems="center"
                >
                  {vehicle.vendor.imageUrl ? (
                    <Box
                      as="img"
                      display="block"
                      objectFit="contain"
                      height={pxToRem(16)}
                      alt={vehicle.vendor.name}
                      src={vehicle.vendor.imageUrl}
                    />
                  ) : (
                    <TextV2
                      color="mono12"
                      typographyVariant="label2"
                    >
                      {vehicle.vendor.name}
                    </TextV2>
                  )}
                  <UserRating
                    reviewCount={rating?.count}
                    minReviewCount={CAR_MIN_REVIEW_COUNT}
                    rating={rating?.overall ? rating.overall * 2 : null}
                  />
                </Flex>
              ) : null}
              <LocationDetail
                kind="dropOff"
                location={vehicle.dropOffLocation}
                searchedLocation={searchedDropOffLocation}
              />
            </Flex>
          </Flex>
        </Flex>

        <PricingPanel
          isExpedia={isExpedia}
          isReserving={isReserving}
          onReserve={() => onReserve(vehicle)}
          isReserveDisabled={isReserveDisabled}
          discountPercentage={discountPercentage}
          formattedTotalRate={formattedTotalRate}
          formattedDailyRate={formattedDailyRate}
          onViewDetails={() => onViewDetails(vehicle)}
          formattedTotalStrikethrough={formattedTotalStrikethrough}
        />
      </Flex>
    </Flex>
  );
};

export const CarListingEntry = React.memo(CarListingEntryInner);
